import groupBy from 'lodash.groupby';
import isEqual from 'lodash.isequal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { startDateField, UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import {
  DataFields,
  DataTypes,
  EmployeeDataFields,
  EvaluationDataFields,
} from '../../../../types';
import {
  getDataFieldWithDataTypeFromKey,
  getKeyFromDataFieldWithDataType,
} from '../../../../utils';
import { BetterSuspense } from '../../BetterSuspense';
import { LoadingCircle } from '../../LoadingCircle';
import { ContextLocation, FilterSection } from '../types';
import { FilterPillsGroup } from './FilterPillsGroup';
import { FilterBarOptions, FilterTypes } from './types';

interface FilterPillsProps<T extends FilterTypes> {
  filtersHandle: GlobalFilterTypeHandle<T>;
  options: FilterBarOptions;
  filterSections: FilterSection[];
}

export const FilterPills = <T extends FilterTypes>(
  props: FilterPillsProps<T>
) => {
  const { t } = useTranslation();
  const { filtersHandle, filterSections, options } = props;
  const [filterState, dispatch] = filtersHandle;
  const filtersGroupedByDimension: Record<string, T[]> = groupBy(
    filterState.items,
    (f) => {
      let dataField = f.property as DataFields;
      // TODO: remove these conditions when we can handle sql filters in movement db
      if (f.dataType === DataTypes.EVALUATION) {
        dataField = EvaluationDataFields.EVALUATION_CYCLE_TYPE;
      }
      // Hire Year filter stores selections as START_CALENDAR_YEAR or START_FINANCIAL_YEAR; map to START_DATE so the pill finds the Hire Year section
      if (
        f.dataType === DataTypes.EMPLOYEE &&
        (dataField === EmployeeDataFields.START_CALENDAR_YEAR ||
          dataField === EmployeeDataFields.START_FINANCIAL_YEAR)
      ) {
        dataField = startDateField.dataField;
      }
      const dataType = f.dataType;
      return getKeyFromDataFieldWithDataType({ dataType, dataField });
    }
  );

  return (
    <>
      <BetterSuspense fallback={<LoadingCircle />}>
        <>
          {Object.entries(filtersGroupedByDimension).map(
            ([dimensionKey, filters]) => {
              const dimension = getDataFieldWithDataTypeFromKey(dimensionKey);
              const section = filterSections.find((fs) =>
                isEqual(fs.dataField, dimension)
              );
              if (section === undefined) {
                return null;
              }
              const filterGroupFilters = filters.map((filter, i) => {
                return {
                  key: `filter-pill-${filter.values.join('-')}-${i}`,
                  color: options.filterPillColors.default,
                  allowDelete: options.allowDelete,
                  originalFilter: {
                    ...filter,
                    // TODO: remove these conditions when we can handle sql filters in movement db
                    label:
                      !filter.sql &&
                      (filter.dataType === DataTypes.EVALUATION ||
                        filter.property === EmployeeDataFields.START_DATE)
                        ? filter.values[0]
                        : filter.label,
                  },
                };
              });
              const filterGroup = {
                key: `filter-group-${dimensionKey}`,
                color: options.filterPillColors.default,
                label: section.labelFn(t, t(UNDEFINED_DISPLAY_KEY), {
                  location: ContextLocation.FILTERBAR,
                }),
                filters: filterGroupFilters,
                filterDataManager: section.filterDataManagerForFilterBar,
              };
              return (
                <FilterPillsGroup<T>
                  key={filterGroup.key}
                  filterGroup={filterGroup}
                  removeFilter={(item: T) =>
                    dispatch({ type: 'remove-single', item })
                  }
                  removeFilters={(items: T[]) =>
                    dispatch({ type: 'remove-multiple', items })
                  }
                />
              );
            }
          )}
        </>
      </BetterSuspense>
    </>
  );
};
